import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface RenameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentName: string
  onSubmit: (name: string) => Promise<void>
  isFolder?: boolean
}

function splitFilename(filename: string): { baseName: string; extension: string } {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot <= 0) return { baseName: filename, extension: '' }
  return {
    baseName: filename.substring(0, lastDot),
    extension: filename.substring(lastDot + 1)
  }
}

export function RenameDialog({ open, onOpenChange, currentName, onSubmit, isFolder = false }: RenameDialogProps) {
  const { t } = useTranslation('assets')
  const { baseName: initialBaseName, extension } = useMemo(
    () => (isFolder ? { baseName: currentName, extension: '' } : splitFilename(currentName)),
    [currentName, isFolder]
  )

  const [baseName, setBaseName] = useState(initialBaseName)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update baseName when dialog opens with new currentName
  useEffect(() => {
    if (open) {
      const { baseName: newBaseName } = isFolder
        ? { baseName: currentName }
        : splitFilename(currentName)
      setBaseName(newBaseName)
    }
  }, [open, currentName, isFolder])

  const fullName = extension ? `${baseName.trim()}.${extension}` : baseName.trim()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!baseName.trim() || fullName === currentName) return

    setIsSubmitting(true)
    try {
      await onSubmit(fullName)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('dialogs.rename.title')}</DialogTitle>
            <DialogDescription>
              {t('dialogs.rename.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-input">{t('dialogs.rename.nameLabel')}</Label>
            {isFolder || !extension ? (
              <Input
                id="rename-input"
                value={baseName}
                onChange={e => setBaseName(e.target.value)}
                className="mt-2"
                autoFocus
              />
            ) : (
              <div className="flex items-center mt-2">
                <Input
                  id="rename-input"
                  value={baseName}
                  onChange={e => setBaseName(e.target.value)}
                  className="rounded-r-none border-r-0"
                  autoFocus
                />
                <div className="flex items-center h-9 px-3 border border-input bg-muted rounded-r-md text-muted-foreground text-sm">
                  .{extension}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!baseName.trim() || fullName === currentName || isSubmitting}
            >
              {isSubmitting ? t('buttons.renaming') : t('buttons.rename')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
